import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getProductDetails } from '../services/productService';
import ProductSettingsModal from '../components/ProductSettingsModal';
import { saveProductBasicSettings } from '../services/productBasicSettings';
import {
  loadPrepayState,
  setPrepayRequired,
  isPrepayRequired,
  isPartialDeposit,
} from '../services/productPrepaySettings';
import { 
  getTourProductResourceList, 
  getTourRatePlanList02,
  saveTourPriceInfo,
  getPriceStockCalendar,
  saveTourSelectedStock,
  createTourProductResource02,
  updateTourProductResource02,
  deleteTourProductResource,
  createTourRatePlanDefault01,
  deleteTourRatePlan,
  getItineraryTypeSimpleList,
  updateTourRatePlanOrder,
} from '../services/apiService';

const formatKRW = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '0';
  return num.toLocaleString('ko-KR');
};

const parseNumber = (str) => {
  const cleaned = String(str || '').replace(/[^0-9]/g, '');
  return cleaned ? Number(cleaned) : 0;
};

export default function GeneralPriceStockSettings() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productInfo, setProductInfo] = useState(null);
  const [resources, setResources] = useState([]); // 재고구분 목록
  const [ratePlans, setRatePlans] = useState({}); // { resourceNo: [요금제 목록] }
  const [selectedResourceNo, setSelectedResourceNo] = useState(null); // 선택된 재고구분
  const [selectedRatePlanNo, setSelectedRatePlanNo] = useState(null); // 선택된 요금제
  const [priceData, setPriceData] = useState({}); // { `${resourceNo}_${ratePlanNo}`: { price, cost, stock } }
  const [currentData, setCurrentData] = useState([]); // 현재 등록된 데이터
  // 상품가격.일정타입번호 는 상품일정타입 을 가리키는 외래키다. 상품을 만들 때 '대표일정'이
  // 한 줄 함께 생기므로 그 번호를 쓴다. 못 찾으면 가격 저장을 막는다 — 아래 저장 함수 참고.
  const [itineraryType, setItineraryType] = useState(null); // { 번호, 이름 }
  // 기본설정은 별도 화면이 아니라 모달이라, 다른 화면들처럼 여기서 직접 띄운다.
  const [basicSettingsOpen, setBasicSettingsOpen] = useState(false);
  const [basicSettingsProduct, setBasicSettingsProduct] = useState(null);
  const [basicSettingsLoading, setBasicSettingsLoading] = useState(false);
  // 기본설정의 신청금 네 칸을 대신하는 체크박스. detail 은 저장 전 상태 판단에도 쓴다.
  const [prepayDetail, setPrepayDetail] = useState(null);
  const [prepaySaving, setPrepaySaving] = useState(false);
  const [resourceStockInput, setResourceStockInput] = useState({}); // { resourceNo: 임시 재고 입력값 }
  const [resourceManageOpen, setResourceManageOpen] = useState(false);
  const [ratePlanManageOpen, setRatePlanManageOpen] = useState(false);
  const [tempResourceName, setTempResourceName] = useState('');
  const [renamingResourceNo, setRenamingResourceNo] = useState(null);
  const [renameResourceValue, setRenameResourceValue] = useState('');
  const [resourceManaging, setResourceManaging] = useState(false);
  const [manageSelectedResourceNo, setManageSelectedResourceNo] = useState(null);
  const [tempRatePlanName, setTempRatePlanName] = useState('');
  const [tempRatePlanMethod, setTempRatePlanMethod] = useState('단품');
  const [ratePlanManaging, setRatePlanManaging] = useState(false);
  // DnD state for table reordering per resource
  const [dragIndexByResource, setDragIndexByResource] = useState({}); // { [resourceNo]: index }
  
  const { companyNo, userNo } = getUserInfo() || {};
  
  // Helper: safely extract resource/rateplan identifiers from varying API shapes
  const getResourceId = (row) => {
    if (!row || typeof row !== 'object') return undefined;
    const cand = [
      row.자원번호, row.resourceNo, row.ResourceNo, row.리소스번호, row.항공패턴번호, row.객실번호, row.id, row.ID, row.no, row.No,
    ];
    const v = cand.find((x) => x != null && String(x).trim() !== '');
    return v != null ? Number(v) : undefined;
  };

  // Commit reordered plan order for a specific resource
  const commitResourcePlanOrder = async (resourceNo, newOrderRows) => {
    try {
      if (!companyNo || !userNo || !productId) return;
      const orderList = (newOrderRows || [])
        .map((row, i) => ({ 가격옵션번호: Number(getRatePlanId(row)), 순번: i + 1 }))
        .filter(v => Number.isFinite(v.가격옵션번호));
      if (!Array.isArray(orderList) || orderList.length === 0) return;
      setRatePlanManaging(true);
      await updateTourRatePlanOrder({
        companyNo: String(companyNo),
        productNo: Number(productId),
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
        orderList,
      });
      await reloadResourcesAndRatePlans();
    } catch (e) {
      console.error('요금제 순서 변경 실패:', e);
      alert('요금제 순서 변경에 실패했습니다.');
    } finally {
      setRatePlanManaging(false);
    }
  };

  const handlePlanDragStartInTable = (resourceNo, index) => {
    setDragIndexByResource(prev => ({ ...prev, [String(resourceNo)]: Number(index) }));
  };
  const handlePlanDragOverInTable = (e) => { try { e.preventDefault(); } catch(_) {} };
  const handlePlanDropInTable = async (resourceNo, dropIndex) => {
    const key = String(resourceNo);
    const from = Number(dragIndexByResource[key]);
    setDragIndexByResource(prev => ({ ...prev, [key]: undefined }));
    if (!Number.isInteger(from) || !Number.isInteger(dropIndex)) return;
    const list = [...(ratePlans[resourceNo] || [])];
    if (from === dropIndex || from < 0 || dropIndex < 0 || from >= list.length || dropIndex >= list.length) return;
    const reordered = [...list];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);
    await commitResourcePlanOrder(resourceNo, reordered);
  };

  // 요금제 순서 변경 (위/아래 이동 시마다 즉시 저장 호출)
  const handleMoveRatePlan = async (planNo, direction) => {
    try {
      const resourceNo = manageSelectedResourceNo;
      if (!resourceNo) return;
      const list = [...(ratePlans[resourceNo] || [])];
      const idx = list.findIndex((p) => getRatePlanId(p) === planNo);
      if (idx === -1) return;
      const delta = direction === 'up' ? -1 : 1;
      const newIndex = idx + delta;
      if (newIndex < 0 || newIndex >= list.length) return;
      // swap
      const tmp = list[idx];
      list[idx] = list[newIndex];
      list[newIndex] = tmp;

      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      setRatePlanManaging(true);
      const orderList = list.map((p, i) => ({ 가격옵션번호: Number(getRatePlanId(p)), 순번: i + 1 }));
      await updateTourRatePlanOrder({
        companyNo: String(companyNo),
        productNo: Number(productId),
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
        orderList,
      });
      await reloadResourcesAndRatePlans();
    } catch (error) {
      console.error('요금제 순서 변경 실패:', error);
      alert('요금제 순서 변경에 실패했습니다.');
    } finally {
      setRatePlanManaging(false);
    }
  };
  const getResourceName = (row) => {
    if (!row || typeof row !== 'object') return '';
    const cand = [row.자원명, row.resourceName, row.ResourceName, row.항공패턴명, row.객실명, row.name, row.이름];
    const v = cand.find((x) => x != null && String(x).trim() !== '');
    return v != null ? String(v) : '';
  };
  const getRatePlanId = (row) => {
    if (!row || typeof row !== 'object') return undefined;
    const cand = [
      row.요금제번호, row.요금번호, row.가격옵션번호, row.ratePlanNo, row.RatePlanNo, row.planNo, row.PlanNo, row.id, row.ID, row.no, row.No,
    ];
    const v = cand.find((x) => x != null && String(x).trim() !== '');
    return v != null ? Number(v) : undefined;
  };
  const getRatePlanName = (row) => {
    if (!row || typeof row !== 'object') return '';
    const cand = [row.요금제명, row.요금명, row.가격옵션명, row.ratePlanName, row.RatePlanName, row.name, row.이름];
    const v = cand.find((x) => x != null && String(x).trim() !== '');
    return v != null ? String(v) : '';
  };
  
  // 단품은 행사일이 없는데 재고·가격은 날짜에 매달려 저장된다. 그래서 "날짜 없음" 자리로
  // 쓸 먼 미래 하루를 정해 거기에 몰아넣는다.
  //
  // 예전에는 이 값이 "올해+1년 12월 31일"이었다. 그러면 해가 바뀔 때마다 저장 위치가 1년씩
  // 밀려서, 작년에 넣은 재고·가격이 화면에서 사라지고 연도별로 유령 재고가 쌓였다.
  // 고정값이어야 하는 이유가 이것이다.
  //
  // 9999-12-31 은 쓰지 않는다. SQL Server DATETIME 의 상한이라 서버가 하루라도 더하는 순간
  // (기간 조회의 DATEADD(DAY,1,@종료일) 같은 흔한 관용구) 오버플로로 죽는다.
  //
  // ⚠ 2036-12-31 이 지나면 이 날짜는 과거가 된다. 그 전에 값을 뒤로 미뤄야 한다.
  const targetDate = '2036-12-31';

  // 이 화면은 단품 전용이라 요금제가 여럿일 이유가 없다. 재고구분을 만들면 서버가 기본요금제를
  // 하나 끼워 넣는데, 그건 투어용(개별요금)이라 단품 가격 구조와 맞지 않는다. 그래서 진입할 때
  // 재고구분마다 단품 요금제 하나만 남도록 맞춘다.
  const SINGLE_ITEM_PLAN_NAME = '일반상품 가격';
  // 요금제구분: 083001 개별요금, 083002 합계요금, 083003 단품
  const PLAN_POLICY_SINGLE_ITEM = '083003';

  /**
   * 단품 요금제인지 판단한다. 여기서 false 인 요금제는 진입할 때 지워진다.
   *
   * 목록 SP 는 코드(가격정책코드)를 줄 때도 있고 이름(가격정책)만 줄 때도 있어 둘 다 본다.
   * 셋 중 아무것도 안 걸리면 false — 서버가 재고구분과 함께 끼워 넣은 기본요금제로 본다.
   * 그래서 요금제 관리에서 손으로 만든 요금제도, 단품 구분이 아니면 다음 진입 때 사라진다.
   * 이 화면이 단품 전용이라 의도한 동작이지만, 요금제를 여러 개 두려면 이 함수부터 봐야 한다.
   */
  const isSingleItemPlan = (row) => {
    if (!row || typeof row !== 'object') return false;
    // 이 화면이 만든 요금제는 이름으로도 알아본다. 목록 SP 가 가격정책을 돌려주지 않는 경우
    // 이름까지 안 보면, 진입할 때마다 새로 만들고 직전 것을 지우는 일이 끝없이 반복된다.
    if (getRatePlanName(row) === SINGLE_ITEM_PLAN_NAME) return true;
    const code = String(row.가격정책코드 ?? '').trim();
    if (code) return code === PLAN_POLICY_SINGLE_ITEM;
    return String(row.가격정책 ?? '').trim() === '단품';
  };

  // 진입 시 한 번만 돈다. 실패해도 다시 시도하지 않는다 — 화면을 열 때마다 같은 오류로
  // 요금제를 만들었다 지웠다 하는 것보다, 조용히 두고 사용자가 요금제 관리에서 손보는 편이 낫다.
  const provisionedProductRef = useRef(null);

  /**
   * 재고구분마다 단품 요금제 하나만 남도록 맞춘다.
   *   1) 단품 요금제가 없으면 '일반상품 가격' 으로 만든다
   *   2) 그 외 요금제(= 서버가 끼워 넣은 기본요금제)를 지운다
   * 무언가 바꿨으면 true 를 돌려준다. 호출한 쪽이 목록을 다시 읽어야 한다.
   */
  const ensureSingleItemPlans = async (resourceList, ratePlanMap) => {
    let changed = false;
    for (const resource of (Array.isArray(resourceList) ? resourceList : [])) {
      const resourceNo = getResourceId(resource);
      if (!resourceNo) continue;
      const plans = ratePlanMap[resourceNo] || [];
      const singleItemPlans = plans.filter(isSingleItemPlan);
      const otherPlans = plans.filter((p) => !isSingleItemPlan(p));

      try {
        if (singleItemPlans.length === 0) {
          await createTourRatePlanDefault01({
            companyNo: String(companyNo),
            productNo: Number(productId),
            planName: SINGLE_ITEM_PLAN_NAME,
            planPolicyCode: PLAN_POLICY_SINGLE_ITEM,
            userNo: String(userNo),
            resourceNo: Number(resourceNo),
          });
          changed = true;

          // 생성 응답을 믿지 않고 목록을 다시 읽어 확인한다. 이 ERP 는 실패해도 HTTP 200 을
          // 주는 경우가 있어, 호출이 돌아왔다는 것만으로는 요금제가 생겼다고 볼 수 없다.
          const after = await getTourRatePlanList02({ companyNo, productNo: productId, resourceNo });
          const created = (Array.isArray(after) ? after : []).some(isSingleItemPlan);
          if (!created) {
            console.warn('[GeneralPriceStock] 단품 요금제 생성을 확인하지 못했다. 기본요금제는 그대로 둔다 (자원번호 ' + resourceNo + ')');
            continue;
          }
        }

        // 단품 요금제를 확보한 뒤에만 지운다. 생성이 실패했는데 지우면 요금제가 하나도 없는
        // 재고구분이 남아, 가격을 넣을 자리 자체가 사라진다.
        for (const plan of otherPlans) {
          const planNo = getRatePlanId(plan);
          if (!planNo) continue;
          await deleteTourRatePlan({
            companyNo: String(companyNo),
            productNo: Number(productId),
            priceOptionNo: Number(planNo),
            userNo: String(userNo),
          });
          changed = true;
        }
      } catch (error) {
        // 한 재고구분이 실패해도 나머지는 계속 맞춘다.
        console.warn('[GeneralPriceStock] 단품 요금제 정리 실패 (자원번호 ' + resourceNo + ')', error);
      }
    }
    return changed;
  };

  // 상품 정보 로드
  useEffect(() => {
    const loadProductInfo = async () => {
      try {
        setLoading(true);
        if (!productId || !companyNo) return;
        
        const details = await getProductDetails(productId);
        setProductInfo(details);

        // 일정타입 — 가격 저장 시 외래키로 넘겨야 한다.
        try {
          const itinData = await getItineraryTypeSimpleList({ productNo: Number(productId) });
          const rows = itinData?.table || itinData?.Table || [];
          const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
          const no = row ? Number(row.일정타입번호) : NaN;
          setItineraryType(Number.isFinite(no) ? { 번호: no, 이름: String(row.일정타입명 || '대표일정') } : null);
        } catch (e) {
          console.warn('[GeneralPriceStock] 일정타입 조회 실패', e);
          setItineraryType(null);
        }

        // 신청금 설정 — 체크박스 상태를 여기서 읽는다.
        try {
          setPrepayDetail(await loadPrepayState(productId));
        } catch (e) {
          console.warn('[GeneralPriceStock] 신청금 설정 조회 실패', e);
          setPrepayDetail(null);
        }
        
        // 재고구분(자원) 목록 조회
        const resourceList = await getTourProductResourceList({ 
          companyNo, 
          productNo: productId 
        });
        const normalizedResources = (Array.isArray(resourceList) ? resourceList : [])
          .map((r) => ({ ...r, __id: getResourceId(r), __name: getResourceName(r) }))
          .filter((r) => r.__id != null);
        try { console.debug('[GeneralPriceStock] resources:', normalizedResources); } catch(_) {}
        setResources(normalizedResources);
        
        // 각 재고구분별 요금제 조회
        const ratePlanMap = {};
        for (const resource of (Array.isArray(normalizedResources) ? normalizedResources : [])) {
          const resourceNo = getResourceId(resource);
          if (resourceNo) {
            const plans = await getTourRatePlanList02({ 
              companyNo, 
              productNo: productId, 
              resourceNo 
            });
            const normalizedPlans = (Array.isArray(plans) ? plans : [])
              .map((p) => ({ ...p, __id: getRatePlanId(p), __name: getRatePlanName(p) }))
              .filter((p) => p.__id != null);
            try { console.debug('[GeneralPriceStock] rate plans for resource', resourceNo, normalizedPlans); } catch(_) {}
            ratePlanMap[resourceNo] = normalizedPlans;
          }
        }
        setRatePlans(ratePlanMap);

        // 단품 요금제 하나만 남도록 정리 (진입 시 1회)
        if (userNo && provisionedProductRef.current !== String(productId)) {
          provisionedProductRef.current = String(productId);
          const changed = await ensureSingleItemPlans(normalizedResources, ratePlanMap);
          if (changed) await reloadResourcesAndRatePlans();
        }

        // 현재 등록된 데이터 조회
        await loadCurrentData();
      } catch (error) {
        console.error('상품 정보 로드 실패:', error);
        alert('상품 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    loadProductInfo();
  }, [productId, companyNo]);
  
  // 현재 등록된 데이터 조회
  const loadCurrentData = async () => {
    try {
      if (!productId || !companyNo) return;
      
      const startDate = targetDate;
      const endDate = targetDate;
      
      const result = await getPriceStockCalendar({
        productTypeName: '투어',
        productNo: productId,
        companyNo,
        startDate,
        endDate,
      });
      
      // API 응답 파싱
      const data = result?.table || result?.Table || [];
      setCurrentData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('현재 데이터 조회 실패:', error);
    }
  };
  
  useEffect(() => {
    if (!ratePlanManageOpen) return;
    if (!Array.isArray(resources) || resources.length === 0) return;
    if (manageSelectedResourceNo != null) return;
    const first = resources.find(r => getResourceId(r) != null);
    const firstNo = first ? getResourceId(first) : null;
    if (firstNo != null) {
      setManageSelectedResourceNo(firstNo);
    }
  }, [ratePlanManageOpen, resources, manageSelectedResourceNo]);
  
  const reloadResourcesAndRatePlans = async () => {
    try {
      if (!productId || !companyNo) return;
      const resourceList = await getTourProductResourceList({ 
        companyNo, 
        productNo: productId,
      });
      const normalizedResources = (Array.isArray(resourceList) ? resourceList : [])
        .map((r) => ({ ...r, __id: getResourceId(r), __name: getResourceName(r) }))
        .filter((r) => r.__id != null);
      try { console.debug('[GeneralPriceStock] resources (reload):', normalizedResources); } catch(_) {}
      setResources(normalizedResources);

      const ratePlanMap = {};
      for (const resource of (Array.isArray(normalizedResources) ? normalizedResources : [])) {
        const resourceNo = getResourceId(resource);
        if (resourceNo) {
          const plans = await getTourRatePlanList02({ 
            companyNo, 
            productNo: productId, 
            resourceNo,
          });
          const normalizedPlans = (Array.isArray(plans) ? plans : [])
            .map((p) => ({ ...p, __id: getRatePlanId(p), __name: getRatePlanName(p) }))
            .filter((p) => p.__id != null);
          try { console.debug('[GeneralPriceStock] rate plans (reload) for resource', resourceNo, normalizedPlans); } catch(_) {}
          ratePlanMap[resourceNo] = normalizedPlans;
        }
      }
      setRatePlans(ratePlanMap);
    } catch (error) {
      console.error('재고구분/요금제 새로고침 실패:', error);
      alert('재고구분 또는 요금제 정보를 새로고침하는 중 오류가 발생했습니다.');
    }
  };

  const handleAddResource = async () => {
    const name = String(tempResourceName || '').trim();
    if (!name) return;
    try {
      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      setResourceManaging(true);
      await createTourProductResource02({
        resourceName: name,
        productNo: String(productId),
        companyNo: String(companyNo),
        userNo: String(userNo),
        대표항공코드: 'NULL',
        재고기준: true,
      });
      setTempResourceName('');
      await reloadResourcesAndRatePlans();
      alert('재고구분이 추가되었습니다.');
    } catch (error) {
      console.error('재고구분 추가 실패:', error);
      alert('재고구분 추가에 실패했습니다.');
    } finally {
      setResourceManaging(false);
    }
  };

  const handleStartRenameResource = (resourceNo, currentName) => {
    setRenamingResourceNo(resourceNo);
    setRenameResourceValue(String(currentName || ''));
  };

  const handleCancelRenameResource = () => {
    setRenamingResourceNo(null);
    setRenameResourceValue('');
  };

  const handleConfirmRenameResource = async () => {
    const name = String(renameResourceValue || '').trim();
    if (!renamingResourceNo || !name) return;
    try {
      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      setResourceManaging(true);
      await updateTourProductResource02({
        resourceName: name,
        productNo: String(productId),
        companyNo: String(companyNo),
        userNo: String(userNo),
        resourceNo: Number(renamingResourceNo),
        대표항공코드: 'NULL',
        재고기준: true,
      });
      setRenamingResourceNo(null);
      setRenameResourceValue('');
      await reloadResourcesAndRatePlans();
      alert('재고구분 이름이 변경되었습니다.');
    } catch (error) {
      console.error('재고구분 이름 변경 실패:', error);
      alert('재고구분 이름 변경에 실패했습니다.');
    } finally {
      setResourceManaging(false);
    }
  };

  const handleDeleteResource = async (resourceNo, resourceName) => {
    if (!resourceNo) return;
    const name = String(resourceName || '').trim();
    const confirmed = window.confirm(name ? `'${name}' 재고구분을 삭제하시겠습니까?` : '이 재고구분을 삭제하시겠습니까?');
    if (!confirmed) return;
    try {
      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      setResourceManaging(true);
      await deleteTourProductResource({
        companyNo: String(companyNo),
        productNo: Number(productId),
        resourceNo: Number(resourceNo),
        userNo: String(userNo),
      });
      await reloadResourcesAndRatePlans();
      if (manageSelectedResourceNo === resourceNo) {
        setManageSelectedResourceNo(null);
      }
      alert('재고구분이 삭제되었습니다.');
    } catch (error) {
      console.error('재고구분 삭제 실패:', error);
      alert('재고구분 삭제에 실패했습니다.');
    } finally {
      setResourceManaging(false);
    }
  };

  const openBasicSettings = async () => {
    try {
      setBasicSettingsLoading(true);
      const details = await getProductDetails(productId, companyNo);
      if (!details) {
        alert('상품 상세 정보를 찾을 수 없습니다.');
        return;
      }
      setBasicSettingsProduct(details);
      setBasicSettingsOpen(true);
    } catch (e) {
      console.error('기본설정 조회 실패:', e);
      alert('상품 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setBasicSettingsLoading(false);
    }
  };

  const handleSaveBasicSettings = async (updatedProduct) => {
    try {
      setBasicSettingsLoading(true);
      const savedNo = await saveProductBasicSettings({
        original: basicSettingsProduct || {},
        updated: updatedProduct,
        companyNo,
        userNo,
      });
      const refreshed = await getProductDetails(savedNo, companyNo).catch(() => null);
      if (refreshed) {
        setBasicSettingsProduct(refreshed);
        setProductInfo(refreshed); // 헤더의 상품명과 저장 시 쓰는 통화코드가 여기서 온다
      }
      alert('성공적으로 저장되었습니다.');
      return true;
    } catch (e) {
      console.error('기본설정 저장 실패:', e);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
      return false;
    } finally {
      setBasicSettingsLoading(false);
    }
  };

  const handleTogglePrepay = async (checked) => {
    // 일부만 받는 신청금이 이미 걸려 있으면 조용히 덮지 않는다. 이 화면에는 그 값을
    // 되돌릴 수단이 없어서, 잘못 누르면 기본설정에 들어가 다시 맞춰야 한다.
    if (checked && isPartialDeposit(prepayDetail)) {
      const ok = window.confirm('이 상품에는 일부 금액만 받는 신청금이 설정돼 있습니다. 전체금액 100%로 덮어쓸까요?');
      if (!ok) return;
    }
    try {
      setPrepaySaving(true);
      const saved = await setPrepayRequired(productId, checked);
      setPrepayDetail(saved);
    } catch (e) {
      console.error('신청금 설정 저장 실패:', e);
      alert('구매시 결제 필수 설정을 저장하지 못했습니다.');
    } finally {
      setPrepaySaving(false);
    }
  };

  const handleAddRatePlan = async () => {
    const name = String(tempRatePlanName || '').trim();
    const resourceNo = manageSelectedResourceNo;
    if (!resourceNo) {
      alert('요금제를 추가할 재고구분을 선택하세요.');
      return;
    }
    if (!name) {
      alert('요금제명을 입력하세요.');
      return;
    }
    try {
      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      const planPolicyCode = '083003'; // 단품 고정
      setRatePlanManaging(true);
      const resp = await createTourRatePlanDefault01({
        companyNo: String(companyNo),
        productNo: Number(productId),
        planName: name,
        planPolicyCode,
        userNo: String(userNo),
        resourceNo: Number(resourceNo),
      });
      try { console.debug('[GeneralPriceStock] create rate plan resp:', resp); } catch(_) {}
      setTempRatePlanName('');
      setTempRatePlanMethod('단품');
      await reloadResourcesAndRatePlans();
      alert('요금제가 추가되었습니다.');
    } catch (error) {
      console.error('요금제 추가 실패:', error);
      alert('요금제 추가에 실패했습니다.');
    } finally {
      setRatePlanManaging(false);
    }
  };

  const handleDeleteRatePlan = async (ratePlanRow) => {
    if (!ratePlanRow) return;
    const ratePlanNo = getRatePlanId(ratePlanRow);
    const ratePlanName = getRatePlanName(ratePlanRow) || '';
    if (!ratePlanNo) return;
    const confirmed = window.confirm(ratePlanName ? `'${ratePlanName}' 요금제를 삭제하시겠습니까?` : '요금제를 삭제하시겠습니까?');
    if (!confirmed) return;
    try {
      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }
      setRatePlanManaging(true);
      const res = await deleteTourRatePlan({
        companyNo: String(companyNo),
        productNo: Number(productId),
        priceOptionNo: Number(ratePlanNo),
        userNo: String(userNo),
      });
      try { console.debug('[GeneralPriceStock] delete rate plan resp:', res); } catch(_) {}
      await reloadResourcesAndRatePlans();
      alert('요금제가 삭제되었습니다.');
    } catch (error) {
      console.error('요금제 삭제 실패:', error);
      alert('요금제 삭제에 실패했습니다.');
    } finally {
      setRatePlanManaging(false);
    }
  };
  
  // 입력값 변경 핸들러
  const handleInputChange = (resourceNo, ratePlanNo, field, value) => {
    const key = `${resourceNo}_${ratePlanNo}`;
    setPriceData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };
  
  // 저장 핸들러
  // values에는 화면에 표시된 최종 값(프리필 + 사용자 편집 반영)이 전달된다.
  const handleSave = async (resourceNo, ratePlanNo, values) => {
    try {
      setSaving(true);
      const key = `${resourceNo}_${ratePlanNo}`;
      const data = priceData[key] || {};

      const rawPrice = values?.price ?? data.price ?? '';
      const rawCost = values?.cost ?? data.cost ?? '';

      const hasAnyValue = [rawPrice, rawCost].some(v => String(v ?? '').trim() !== '');
      if (!hasAnyValue) {
        alert('입력된 데이터가 없습니다.');
        return;
      }
      
      const resource = resources.find(r => getResourceId(r) === resourceNo);
      const ratePlan = (ratePlans[resourceNo] || []).find(rp => getRatePlanId(rp) === ratePlanNo);
      
      if (!resource || !ratePlan) {
        alert('재고구분 또는 요금제 정보를 찾을 수 없습니다.');
        return;
      }

      // 상품가격.일정타입번호 는 상품일정타입 을 가리키는 외래키다. 없는 번호(예전에는 0을
      // 그대로 보냈다)를 넣으면 서버가 FK_상품가격_일정타입번호 위반으로 저장을 통째로 거부한다.
      // 번호를 모르면 보내지 않는다 — 실패할 요청을 만들어 오류창을 띄우는 것보다 낫다.
      if (!itineraryType || !Number.isFinite(Number(itineraryType.번호))) {
        alert('이 상품의 일정타입을 찾을 수 없어 가격을 저장할 수 없습니다. 상품을 다시 열어보고, 계속 같으면 관리자에게 알려주세요.');
        return;
      }
      
      // 가격 정보 저장 (@가격정보 JSON 구조 - 투어와 동일 양식)
      const adultPrice = parseNumber(rawPrice) || 0;
      const adultCost = parseNumber(rawCost) || 0;

      const ratePlanName = getRatePlanName(ratePlan) || '요금제';
      const resourceName = getResourceName(resource) || '재고구분';

      const currencyCode = String(productInfo?.통화코드 || '058001');
      const currencySymbol = String(productInfo?.통화기호 || '￦');
      const priceInfo = {
        상품번호: Number(productId),
        전일종가: 1,
        상품일정타입: String(productInfo?.상품일정타입 || ''),
        상품일정타입코드: String(productInfo?.상품일정타입코드 || ''),
        적용환율: [
          { 통화코드: currencyCode, 통화기호: currencySymbol, 적용환율: 1, 실시간환율: 1, 평균환율: 1 },
        ],
        통화코드: currencyCode,
        통화기호: currencySymbol,
        노출채널: [
          { 선택: true, 코드: '웹노출여부', 코드명: '웹노출여부' },
          { 선택: true, 코드: '대리점노출여부', 코드명: '대리점노출여부' },
        ],
        선택일정타입: itineraryType.번호,
        일정타입: [ { 선택: false, 코드: itineraryType.번호, 코드명: itineraryType.이름 } ],
        랜드사: [],
        상품자원요금제: [
          {
            자원번호: Number(resourceNo),
            자원명: String(resourceName),
            요금제리스트: [
              { 가격옵션번호: Number(ratePlanNo), 가격옵션명: String(ratePlanName), 합산가격여부: 'Y' },
            ],
          },
        ],
        선택요금제: [ { 가격옵션번호: Number(ratePlanNo), 합산가격여부: 'Y' } ],
        요금제: [ { 요금제번호: Number(ratePlanNo), 요금제명: String(ratePlanName) } ],
        판매마감일기준: '1',
        판매마감특정일: '',
        판매마감일디폴트몇일전: 0,
        가상모객사용: false,
        가상모객인원: 0,
        가상모객출발몇일전: 0,
        원가구분: '1',
        총원가: [
          {
            구분: '총원가',
            통화코드: currencyCode,
            통화기호: currencySymbol,
            성인: Number(adultCost),
            어린이: 0,
            영유아: 0,
          },
        ],
        세부원가: [
          {
            원가항목: '',
            원가항목코드: '',
            통화기호: '',
            통화코드: '',
            환율적용방식: '',
            환율적용방식코드: '',
            적용환율: '',
            성인: 0,
            어린이: 0,
            영유아: 0,
          },
        ],
        판매가: [
          {
            항목: '판매가',
            번호: 1,
            통화코드: currencyCode,
            통화기호: currencySymbol,
            성인: Number(adultPrice),
            어린이: 0,
            영유아: 0,
          },
          {
            항목: 'Tax및유류할증료',
            번호: 2,
            통화코드: currencyCode,
            통화기호: currencySymbol,
            성인: 0,
            어린이: 0,
            영유아: 0,
          },
          {
            항목: '커미션',
            번호: 3,
            통화코드: currencyCode,
            통화기호: currencySymbol,
            성인: 0,
            어린이: 0,
            영유아: 0,
          },
        ],
        특별약관사용: false,
        특별약관간략정보: '',
        취소료: [ { 기준: '출발일', 몇일전: 0, 고정날짜: '', 취소요율: 0 } ],
        행사시작시간: [],
        선택시작시간: [],
        적용기간: [ { 시작일: String(targetDate), 종료일: String(targetDate), 요일: ['월','화','수','목','금','토','일'], 요일2: 'YYYYYYY' } ],
        플랫폼수수료적용여부: true,
        플랫폼수수료: 0.01,
        결제수수료적용여부: true,
        결제수수료: 0.03,
        기존데이터업데이트여부: true,
        합산가격여부: true,
        커미션타입: '정률',
      };

      const priceInfoJson = JSON.stringify(priceInfo);
      await saveTourPriceInfo({ priceInfoJson, userNo, companyNo, 합산가격여부: true });
      
      alert('저장되었습니다.');
      
      // 입력 필드 초기화
      setPriceData(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      
      // 현재 데이터 새로고침
      await loadCurrentData();
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
    } finally {
      setSaving(false);
    }
  };

  // 재고구분 단위 재고 저장 핸들러
  const handleSaveResourceStock = async (resourceNo) => {
    try {
      const rawStock = getResourceDisplayStock(resourceNo);
      if (String(rawStock ?? '').trim() === '') {
        alert('재고 수량을 입력하세요.');
        return;
      }

      if (!companyNo || !userNo || !productId) {
        alert('업체 정보 또는 사용자 정보가 없습니다.');
        return;
      }

      const stockCount = parseNumber(rawStock) || 0;

      setSaving(true);

      const events = [{ 날짜: String(targetDate), 시간: '00:00', 자원번호: Number(resourceNo) }];
      await saveTourSelectedStock({
        productNo: Number(productId),
        companyNo: String(companyNo),
        userNo: String(userNo),
        stockCount: String(stockCount),
        events,
        calendarUse: true,
        seriesUpdate: false,
      });

      // 입력값은 currentData로부터 다시 가져오도록 초기화
      setResourceStockInput(prev => {
        const next = { ...prev };
        delete next[String(resourceNo)];
        return next;
      });

      await loadCurrentData();
      alert('재고가 저장되었습니다.');
    } catch (error) {
      console.error('재고 저장 실패:', error);
      alert('재고 저장에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
    } finally {
      setSaving(false);
    }
  };
  
  // 현재 데이터에서 특정 재고구분/요금제의 데이터 찾기
  const getCurrentDataFor = (resourceNo, ratePlanNo) => {
    return (Array.isArray(currentData) ? currentData : []).find(item => {
      const rno = getResourceId(item);
      const pno = getRatePlanId(item);
      return rno === resourceNo && pno === ratePlanNo;
    });
  };
  
  const getResourceSummaryItem = (resourceNo) => {
    return (Array.isArray(currentData) ? currentData : []).find(item => {
      const rno = getResourceId(item);
      return rno === resourceNo;
    }) || null;
  };

  const getResourceDisplayStock = (resourceNo) => {
    const key = String(resourceNo);
    if (Object.prototype.hasOwnProperty.call(resourceStockInput, key)) {
      return resourceStockInput[key];
    }
    const summary = getResourceSummaryItem(resourceNo);
    return summary?.보유석 ?? '';
  };

  const handleResourceStockChange = (resourceNo, value) => {
    setResourceStockInput(prev => ({
      ...prev,
      [String(resourceNo)]: value,
    }));
  };

  const getResourceSaleCount = (resourceNo) => {
    const item = getResourceSummaryItem(resourceNo);
    if (!item) return '';
    const sale = item.판매수량 ?? item.모객수량 ?? item.예약수 ?? '';
    const s = String(sale ?? '').trim();
    return s !== '' ? s : '';
  };

  const getResourceRemainCount = (resourceNo) => {
    const item = getResourceSummaryItem(resourceNo);
    if (!item) return '';
    const remain = item.잔여석 ?? item.잔여 ?? '';
    const s = String(remain ?? '').trim();
    return s !== '' ? s : '';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {basicSettingsOpen && (
        <ProductSettingsModal
          product={basicSettingsProduct}
          onClose={async () => {
            setBasicSettingsOpen(false);
            setBasicSettingsProduct(null);
            // 모달 안에서 신청금을 직접 고쳤을 수 있다. 체크박스를 실제 값에 다시 맞춘다.
            try {
              setPrepayDetail(await loadPrepayState(productId));
            } catch (_) {}
          }}
          onSave={handleSaveBasicSettings}
          loading={basicSettingsLoading}
          hideBasicCity
          hideBasicDuration
          hideDetailTab
        />
      )}
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            
            <p className="text-sm text-gray-600 mt-1">
              {productInfo?.상품명} (상품번호: {productId})
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openBasicSettings}
              disabled={basicSettingsLoading}
              className="px-3 py-2 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              기본설정
            </button>
            <button
              type="button"
              onClick={() => navigate(`/general/detail-settings/${productId}`)}
              className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
            >
              상세설정
            </button>
            <button
              type="button"
              disabled
              title="현재 화면"
              className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-white text-gray-700 opacity-50"
            >
              가격/재고
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              뒤로가기
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
          
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setResourceManageOpen(true)}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              재고구분 관리
            </button>
            <button
              type="button"
              onClick={() => setRatePlanManageOpen(true)}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              요금제 관리
            </button>
            <label
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded border border-gray-300 bg-white cursor-pointer select-none"
              title="켜면 기본설정의 신청금이 전체금액 100%, 예약일 기준 0일 후까지로 설정됩니다. (잔금 완납 조건은 건드리지 않습니다)"
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={isPrepayRequired(prepayDetail)}
                disabled={prepaySaving || !prepayDetail}
                onChange={(e) => handleTogglePrepay(e.target.checked)}
              />
              <span className="text-gray-700">구매시 결제 필수</span>
              {prepaySaving && <span className="text-gray-400">저장 중...</span>}
            </label>
          </div>
        </div>
      </div>
      
      {/* 재고구분별 섹션 */}
      <div className="space-y-6">
        {/* 재고구분 / 요금제 선택 영역 (현재는 UI 숨김 처리) */}
        <div className="hidden bg-white border rounded-lg p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 font-medium">재고구분 선택</span>
            <select
              className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white"
              value={selectedResourceNo ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSelectedResourceNo(null);
                  // 재고구분 전체 보기로 바꿀 때 요금제 필터도 해제
                  setSelectedRatePlanNo(null);
                  return;
                }
                const nextResourceNo = Number(value) || null;
                setSelectedResourceNo(nextResourceNo);
                if (nextResourceNo != null) {
                  const plansForResource = ratePlans[nextResourceNo] || [];
                  const firstPlan = plansForResource[0];
                  const firstPlanNo = firstPlan ? getRatePlanId(firstPlan) : null;
                  setSelectedRatePlanNo(firstPlanNo ?? null);
                } else {
                  setSelectedRatePlanNo(null);
                }
              }}
            >
              <option value="">전체 재고구분</option>
              {resources.length === 0 && <option value="" disabled>재고구분 없음</option>}
              {resources.map((r) => {
                const rno = getResourceId(r);
                const rname = getResourceName(r) || '이름 없음';
                return (
                  <option key={rno} value={rno}>{rname}</option>
                );
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 font-medium">요금제 선택</span>
            <select
              className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white min-w-[180px]"
              value={selectedRatePlanNo ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  // 모든 요금제 보기
                  setSelectedRatePlanNo(null);
                  return;
                }
                const nextPlanNo = Number(value) || null;
                setSelectedRatePlanNo(nextPlanNo);
              }}
            >
              <option value="">모든 요금제</option>
              {(!selectedResourceNo || (ratePlans[selectedResourceNo] || []).length === 0) && (
                <option value="" disabled>요금제 없음</option>
              )}
              {(ratePlans[selectedResourceNo] || []).map((p) => {
                const pno = getRatePlanId(p);
                const pname = getRatePlanName(p) || '이름 없음';
                return (
                  <option key={pno} value={pno}>{pname}</option>
                );
              })}
            </select>
          </div>
        </div>

        {resources.length === 0 ? (
          <div className="bg-white border rounded-lg p-8 text-center">
            <p className="text-gray-500">등록된 재고구분이 없습니다.</p>
            <p className="text-sm text-gray-400 mt-2">상품 설정에서 재고구분을 먼저 등록해주세요.</p>
          </div>
        ) : (
          resources
            .filter(resource => {
              const resourceNo = getResourceId(resource);
              if (!selectedResourceNo) return true;
              return resourceNo === selectedResourceNo;
            })
            .map(resource => {
            const resourceNo = getResourceId(resource);
            const resourceName = getResourceName(resource) || '이름 없음';
            const plans = ratePlans[resourceNo] || [];
            
            return (
              <div key={resourceNo} className="bg-white">
                {/* 재고구분 헤더 + 통합 재고/잔여 */}
                <div className="px-0 py-4 space-y-2">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    재고구분: {resourceName}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">재고</span>
                      <input
                        type="text"
                        value={String(getResourceDisplayStock(resourceNo) ?? '')}
                        onChange={(e) => handleResourceStockChange(resourceNo, e.target.value)}
                        placeholder="0"
                        className="w-28 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <button
                        type="button"
                        className="ml-2 px-3 py-1.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                        onClick={() => handleSaveResourceStock(resourceNo)}
                        disabled={saving || String(getResourceDisplayStock(resourceNo) ?? '').trim() === ''}
                      >
                        재고 저장
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">잔여수량</span>
                      <span className="text-sm text-gray-800">
                        {(() => {
                          const v = getResourceRemainCount(resourceNo);
                          return v !== '' ? v : '-';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* 요금제 테이블 */}
                <div className="p-0">
                  {plans.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">등록된 요금제가 없습니다.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full w-full table-fixed divide-y divide-gray-200">
                        <colgroup>
                          <col className="w-[36%]" />
                          <col className="w-[18%]" />
                          <col className="w-[18%]" />
                          <col className="w-[14%]" />
                          <col className="w-[14%]" />
                        </colgroup>
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              요금제
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              가격 (성인)
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              원가
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              판매수량
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              저장
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {plans
                            .filter(plan => {
                              const ratePlanNo = getRatePlanId(plan);
                              if (!selectedRatePlanNo) return true;
                              return ratePlanNo === selectedRatePlanNo;
                            })
                            .map((plan, planIndex) => {
                              const ratePlanNo = getRatePlanId(plan);
                              const ratePlanName = getRatePlanName(plan) || '이름 없음';
                              const key = `${resourceNo}_${ratePlanNo}`;
                              const inputData = priceData[key] || {};
                              const currentItem = getCurrentDataFor(resourceNo, ratePlanNo);

                              const resolvedPriceFromCurrent = (() => {
                                if (!currentItem) return '';
                                const primary = currentItem.성인판매가 ?? currentItem.판매가성인 ?? currentItem.판매가;
                                if (primary != null && String(primary).trim() !== '') return primary;
                                const total = currentItem.총판매가 ?? currentItem.TotalPrice;
                                if (!total) return '';
                                const first = String(total).split('/')[0]?.trim();
                                return first || '';
                              })();

                              const resolvedCostFromCurrent = (() => {
                                if (!currentItem) return '';
                                const primary = currentItem.성인원가 ?? currentItem.원가성인 ?? currentItem.원가;
                                if (primary != null && String(primary).trim() !== '') return primary;
                                const total = currentItem.원가 ?? currentItem.총원가 ?? currentItem.TotalCost;
                                if (!total) return '';
                                const first = String(total).split('/')[0]?.trim();
                                return first || '';
                              })();

                              const displayPrice = inputData.price !== undefined ? inputData.price : resolvedPriceFromCurrent;
                              const displayCost = inputData.cost !== undefined ? inputData.cost : resolvedCostFromCurrent;
                              const unifiedStock = getResourceDisplayStock(resourceNo);
                              
                              return (
                                <tr
                                  key={ratePlanNo}
                                  className="hover:bg-gray-50"
                                  draggable
                                  onDragStart={() => handlePlanDragStartInTable(resourceNo, planIndex)}
                                  onDragOver={handlePlanDragOverInTable}
                                  onDrop={() => handlePlanDropInTable(resourceNo, planIndex)}
                                >
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className="cursor-grab select-none text-gray-400">≡</span>
                                      <div className="text-sm font-medium text-gray-900">{ratePlanName}</div>
                                    </div>
                                  
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <input
                                      type="text"
                                      value={String(displayPrice ?? '')}
                                      onChange={(e) => handleInputChange(resourceNo, ratePlanNo, 'price', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <input
                                      type="text"
                                      value={String(displayCost ?? '')}
                                      onChange={(e) => handleInputChange(resourceNo, ratePlanNo, 'cost', e.target.value)}
                                      placeholder="0"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-center">
                                    {(() => {
                                      const sale = currentItem?.판매수량 ?? currentItem?.모객수량 ?? currentItem?.예약수 ?? '';
                                      const s = String(sale ?? '').trim();
                                      return s !== '' ? s : '-';
                                    })()}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-center">
                                    <button
                                      onClick={() => handleSave(resourceNo, ratePlanNo, {
                                        price: displayPrice,
                                        cost: displayCost,
                                      })}
                                      disabled={saving || !(
                                        String(displayPrice ?? '').trim() !== '' ||
                                        String(displayCost ?? '').trim() !== ''
                                      )}
                                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {saving ? '저장 중...' : '저장'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      {resourceManageOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!resourceManaging) setResourceManageOpen(false);
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-[620px] max-w-[95vw] max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-800">재고구분 관리</h3>
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                disabled={resourceManaging}
                onClick={() => setResourceManageOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">새 재고구분 추가</span>
                  <span className="text-xs text-gray-500">{Array.isArray(resources) ? resources.length : 0}개</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 px-2 py-1 text-sm border rounded"
                    placeholder="예: 기초상품자원"
                    value={tempResourceName}
                    onChange={(e) => setTempResourceName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddResource(); }}
                    disabled={resourceManaging}
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                    onClick={handleAddResource}
                    disabled={resourceManaging}
                  >
                    추가
                  </button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">재고구분 목록</span>
                </div>
                <div className="border rounded divide-y">
                  {(Array.isArray(resources) ? resources : []).map((r) => {
                    const rno = getResourceId(r);
                    const rname = getResourceName(r) || '이름 없음';
                    if (!rno) return null;
                    const isRenaming = renamingResourceNo === rno;
                    const isSelected = manageSelectedResourceNo === rno;
                    return (
                      <div
                        key={rno}
                        className={`flex items-center gap-2 px-3 py-2 bg-white ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        {isRenaming ? (
                          <>
                            <input
                              type="text"
                              className="flex-1 px-2 py-1 text-sm border rounded"
                              value={renameResourceValue}
                              onChange={(e) => setRenameResourceValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmRenameResource();
                                if (e.key === 'Escape') handleCancelRenameResource();
                              }}
                              autoFocus
                              disabled={resourceManaging}
                            />
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                              onClick={handleConfirmRenameResource}
                              disabled={resourceManaging}
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                              onClick={handleCancelRenameResource}
                              disabled={resourceManaging}
                            >
                              취소
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="flex-1 text-left min-w-0"
                              onClick={() => setManageSelectedResourceNo(rno)}
                              disabled={resourceManaging}
                            >
                              <div className="text-sm text-gray-800 truncate">{rname}</div>
                              <div className="text-[11px] text-gray-400">자원번호: {rno}</div>
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="px-2 py-1 text-xs rounded border hover:bg-gray-50"
                                onClick={() => handleStartRenameResource(rno, rname)}
                                disabled={resourceManaging}
                              >
                                이름변경
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1 text-xs rounded border text-red-600 hover:bg-red-50 disabled:opacity-40"
                                onClick={() => handleDeleteResource(rno, rname)}
                                disabled={resourceManaging}
                              >
                                삭제
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {(!resources || resources.length === 0) && (
                    <div className="px-3 py-8 text-sm text-gray-500 text-center">재고구분이 없습니다. 상단에서 추가하세요.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {ratePlanManageOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!ratePlanManaging) setRatePlanManageOpen(false);
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-[860px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-800">요금제 관리</h3>
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                disabled={ratePlanManaging}
                onClick={() => setRatePlanManageOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="flex divide-x" style={{ maxHeight: '75vh' }}>
              <div className="w-[38%] p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600">재고구분</span>
                  <span className="text-xs text-gray-500">{Array.isArray(resources) ? resources.length : 0}개</span>
                </div>
                <div className="space-y-2">
                  {(Array.isArray(resources) ? resources : []).map((r) => {
                    const rno = getResourceId(r);
                    const rname = getResourceName(r) || '이름 없음';
                    if (!rno) return null;
                    const plansForResource = ratePlans[rno] || [];
                    const isSelected = manageSelectedResourceNo === rno;
                    return (
                      <div key={rno} className="border rounded overflow-hidden">
                        <button
                          type="button"
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm ${isSelected ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'}`}
                          onClick={() => setManageSelectedResourceNo(rno)}
                          disabled={ratePlanManaging}
                        >
                          <span className="truncate text-gray-800">{rname}</span>
                          <span className="text-[11px] text-gray-500">{plansForResource.length}개</span>
                        </button>
                      </div>
                    );
                  })}
                  {(!resources || resources.length === 0) && (
                    <div className="px-3 py-8 text-sm text-gray-500 text-center border rounded">재고구분이 없습니다.</div>
                  )}
                </div>
              </div>
              <div className="w-[62%] p-4 overflow-y-auto">
                <div className="mb-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-gray-600">
                      선택된 재고구분: <span className="font-medium text-gray-800">{(() => {
                        const r = (Array.isArray(resources) ? resources : []).find(x => getResourceId(x) === manageSelectedResourceNo);
                        return r ? (getResourceName(r) || '이름 없음') : '-';
                      })()}</span>
                    </span>
                  </div>
                  {!manageSelectedResourceNo ? (
                    <div className="px-3 py-10 text-sm text-gray-500 text-center border border-dashed rounded">
                      좌측에서 재고구분을 먼저 선택하세요.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="font-bold block text-xs text-gray-600 mb-1">새 요금제 추가</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div className="sm:col-span-2 flex gap-2">
                            <input
                              type="text"
                              className="flex-1 px-2 py-1 text-sm border rounded"
                              placeholder="요금제명을 입력하세요"
                              value={tempRatePlanName}
                              onChange={(e) => setTempRatePlanName(e.target.value)}
                              disabled={ratePlanManaging}
                            />
                          </div>
                          <div className="flex gap-2">
                            <select
                              className="flex-1 px-2 py-1 text-sm border rounded bg-gray-50 text-gray-600"
                              value={tempRatePlanMethod}
                              disabled
                            >
                              <option value="단품">단품</option>
                            </select>
                            <button
                              type="button"
                              className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                              onClick={handleAddRatePlan}
                              disabled={ratePlanManaging}
                            >
                              추가
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600">요금제 목록</span>
                          <span className="text-xs text-gray-500">{(() => {
                            const list = ratePlans[manageSelectedResourceNo] || [];
                            return Array.isArray(list) ? list.length : 0;
                          })()}개</span>
                        </div>
                        <div className="border rounded divide-y">
                          {(() => {
                            const list = ratePlans[manageSelectedResourceNo] || [];
                            if (!Array.isArray(list) || list.length === 0) {
                              return (
                                <div className="px-3 py-8 text-sm text-gray-500 text-center">요금제가 없습니다. 상단에서 추가하세요.</div>
                              );
                            }
                            return list.map((p) => {
                              const pno = getRatePlanId(p);
                              const pname = getRatePlanName(p) || '이름 없음';
                              return (
                                <div key={pno || pname} className="flex items-center gap-2 px-3 py-2 bg-white">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-800 truncate">{pname}</div>
                                    <div className="text-[11px] text-gray-400">가격옵션번호: {pno || '-'}</div>
                                  </div>
                                  <button
                                    type="button"
                                    className="px-2 py-1 text-xs rounded border text-red-600 hover:bg-red-50 disabled:opacity-40"
                                    onClick={() => handleDeleteRatePlan(p)}
                                    disabled={ratePlanManaging}
                                  >
                                    삭제
                                  </button>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
